import React from 'react'
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Chip,
  Stack,
  CircularProgress,
  Box,
  Button,
  Typography,
} from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import ArchiveIcon from '@mui/icons-material/Archive'
import PreviewIcon from '@mui/icons-material/Preview'
import VisibilityIcon from '@mui/icons-material/Visibility'
import AddIcon from '@mui/icons-material/Add'
import { FormTemplate } from '@/core/models/form.model'
import Pagination from '@/components/ui/pagination/Pagination'
import { t } from '@lingui/macro'

interface FormTemplatesListProps {
  templates: FormTemplate[]
  totalCount: number
  page: number
  pageSize: number
  isLoading?: boolean
  onEdit: (template: FormTemplate) => void
  onArchive: (template: FormTemplate) => void
  onPreview: (template: FormTemplate) => void
  onView: (template: FormTemplate) => void
  onCreateNew: () => void
  onPageChange: (page: number, itemsPerPage: number) => void
  onPageSizeChange: (pageSize: number) => void
}

const FormTemplatesListComponent: React.FC<FormTemplatesListProps> = ({
  templates,
  totalCount,
  page,
  pageSize,
  isLoading,
  onEdit,
  onArchive,
  onPreview,
  onView,
  onCreateNew,
  onPageChange,
  onPageSizeChange,
}) => {
  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Stack spacing={3}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2 }}>
        <Pagination
          totalItems={totalCount}
          page={page}
          onPageChange={onPageChange}
          onItemsPerPageChange={onPageSizeChange}
          defaultItemsPerPage={pageSize}
          itemsPerPageOptions={[5, 10, 25]}
        />
        <Button variant="contained" startIcon={<AddIcon />} onClick={onCreateNew}>
          {t`Create new template`}
        </Button>
      </Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell width="20%">{t`Title`}</TableCell>
              <TableCell width="30%">{t`Description`}</TableCell>
              <TableCell width="10%">{t`Status`}</TableCell>
              <TableCell width="15%">{t`Categories`}</TableCell>
              <TableCell width="15%">{t`Tags`}</TableCell>
              <TableCell width="5%">{t`Fields`}</TableCell>
              <TableCell width="5%" align="right">{t`Actions`}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {templates.map((template) => (
              <TableRow key={template._id}>
                <TableCell>
                  <Typography noWrap>{template.title}</Typography>
                </TableCell>
                <TableCell>
                  <Typography
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                    }}
                  >
                    {template.description}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip label={template.isActive ? t`Active` : t`Archived`} color={template.isActive ? 'success' : 'default'} size="small" />
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
                    {template.categories?.map((category) => (
                      <Chip key={category} label={category} size="small" color="primary" variant="outlined" />
                    ))}
                  </Stack>
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
                    {template.tags?.map((tag) => (
                      <Chip key={tag} label={tag} size="small" color="secondary" variant="outlined" />
                    ))}
                  </Stack>
                </TableCell>
                <TableCell align="center">{template.fields.length}</TableCell>
                <TableCell>
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Tooltip title={t`View`}>
                      <IconButton size="small" onClick={() => onView(template)} color="primary">
                        <VisibilityIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t`Preview`}>
                      <IconButton size="small" onClick={() => onPreview(template)}>
                        <PreviewIcon />
                      </IconButton>
                    </Tooltip>
                    {template.isActive && (
                      <>
                        <Tooltip title={t`Edit`}>
                          <IconButton size="small" onClick={() => onEdit(template)}>
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t`Archive`}>
                          <IconButton size="small" color="warning" onClick={() => onArchive(template)}>
                            <ArchiveIcon />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  )
}

export default FormTemplatesListComponent
